import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase";

const Announcements = () => {
    const navigate = useNavigate();
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // "all", "active", "inactive"
    const [audienceFilter, setAudienceFilter] = useState("all"); // "all", "user", "partner", "both"
    const [priorityFilter, setPriorityFilter] = useState("all"); // "all", "high", "medium", "low"
    const [sortBy, setSortBy] = useState("newest"); // "newest", "oldest", "priority", "title"

    useEffect(() => {
        setLoading(true);
        setError(null);

        // Set up real-time listener
        const announcementsRef = collection(db, "announcements");
        const q = query(announcementsRef, orderBy("createdAt", "desc"));

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                try {
                    const records = [];
                    snapshot.forEach((doc) => {
                        records.push({ id: doc.id, ...doc.data() });
                    });
                    setAnnouncements(records);
                    setLoading(false);
                    setError(null);
                } catch (err) {
                    console.error("Error processing announcements:", err);
                    setError(err.message);
                    setLoading(false);
                }
            },
            (err) => {
                console.error("Error listening to announcements:", err);
                setError(err.message);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, []);

    const handleDelete = async (id, title) => {
        if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
            return;
        }

        try {
            await deleteDoc(doc(db, "announcements", id));
            // State will update automatically via listener
        } catch (err) {
            console.error("Error deleting announcement:", err);
            alert("Failed to delete announcement. Please try again.");
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            await updateDoc(doc(db, "announcements", id), {
                isActive: !currentStatus,
                updatedAt: Timestamp.now()
            });
            // State will update automatically via listener
        } catch (err) {
            console.error("Error updating announcement status:", err);
            alert("Failed to update announcement status. Please try again.");
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";
        let date;
        if (timestamp._seconds) {
            date = new Date(timestamp._seconds * 1000);
        } else if (timestamp.toMillis) {
            date = new Date(timestamp.toMillis());
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else {
            return "N/A";
        }
        return date.toLocaleString();
    };

    const filteredAndSortedAnnouncements = useMemo(() => {
        let filtered = announcements;

        // Search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (announcement) =>
                    announcement.title?.toLowerCase().includes(searchLower) ||
                    announcement.description?.toLowerCase().includes(searchLower)
            );
        }

        // Status filter
        if (statusFilter !== "all") {
            filtered = filtered.filter((announcement) => {
                if (statusFilter === "active") return announcement.isActive === true;
                if (statusFilter === "inactive") return announcement.isActive === false;
                return true;
            });
        }

        // Audience filter
        if (audienceFilter !== "all") {
            filtered = filtered.filter(
                (announcement) => announcement.targetAudience === audienceFilter
            );
        }

        // Priority filter
        if (priorityFilter !== "all") {
            filtered = filtered.filter(
                (announcement) => announcement.priority === priorityFilter
            );
        }

        // Sort
        const sorted = [...filtered].sort((a, b) => {
            if (sortBy === "newest") {
                const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?._seconds ? a.createdAt._seconds * 1000 : 0);
                const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?._seconds ? b.createdAt._seconds * 1000 : 0);
                return bTime - aTime;
            } else if (sortBy === "oldest") {
                const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?._seconds ? a.createdAt._seconds * 1000 : 0);
                const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?._seconds ? b.createdAt._seconds * 1000 : 0);
                return aTime - bTime;
            } else if (sortBy === "priority") {
                const priorityOrder = { high: 3, medium: 2, low: 1 };
                return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
            } else if (sortBy === "title") {
                return (a.title || "").localeCompare(b.title || "");
            }
            return 0;
        });

        return sorted;
    }, [announcements, searchTerm, statusFilter, audienceFilter, priorityFilter, sortBy]);

    if (loading) {
        return (
            <div className="announcements-container">
                <div className="loading-container">
                    <span className="loader"></span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="announcements-container">
                <div className="error-container">
                    <p>Error: {error}</p>
                    <p className="error-note">The connection will automatically retry. Please refresh the page if the error persists.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="announcements-container">
            <div className="header">
                <h2>New Features</h2>
                <button className="new-announcement-btn" onClick={() => navigate("/announcements/new")}>
                    + New Announcement
                </button>
            </div>

            <div className="filters-section">
                <div className="filter-group">
                    <input
                        type="text"
                        placeholder="Search by title or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="filter-group">
                    <label>Status:</label>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Audience:</label>
                    <select value={audienceFilter} onChange={(e) => setAudienceFilter(e.target.value)}>
                        <option value="all">All</option>
                        <option value="user">User</option>
                        <option value="partner">Partner</option>
                        <option value="both">Both</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Priority:</label>
                    <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                        <option value="all">All</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Sort By:</label>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="priority">Priority</option>
                        <option value="title">Title (A-Z)</option>
                    </select>
                </div>
            </div>

            <div className="announcements-count">
                Showing {filteredAndSortedAnnouncements.length} of {announcements.length} announcement(s)
            </div>

            {filteredAndSortedAnnouncements.length === 0 ? (
                <div className="empty-container">
                    <p>No announcements found.</p>
                </div>
            ) : (
                <div className="announcements-table-container">
                    <table className="announcements-table">
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Target Audience</th>
                                <th>Priority</th>
                                <th>Status</th>
                                <th>Created Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAndSortedAnnouncements.map((announcement) => (
                                <tr key={announcement.id}>
                                    <td className="title-cell">
                                        <strong>{announcement.title || "Untitled"}</strong>
                                        {announcement.description && (
                                            <div className="description-preview">
                                                {announcement.description.length > 100
                                                    ? `${announcement.description.substring(0, 100)}...`
                                                    : announcement.description}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`audience-badge audience-${announcement.targetAudience || "user"}`}>
                                            {(announcement.targetAudience || "user").charAt(0).toUpperCase() + (announcement.targetAudience || "user").slice(1)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`priority-badge priority-${announcement.priority || "medium"}`}>
                                            {(announcement.priority || "medium").charAt(0).toUpperCase() + (announcement.priority || "medium").slice(1)}
                                        </span>
                                    </td>
                                    <td>
                                        <label className="status-toggle">
                                            <input
                                                type="checkbox"
                                                checked={announcement.isActive === true}
                                                onChange={() => handleToggleStatus(announcement.id, announcement.isActive)}
                                            />
                                            <span className={`status-badge ${announcement.isActive ? "active" : "inactive"}`}>
                                                {announcement.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </label>
                                    </td>
                                    <td>{formatDate(announcement.createdAt)}</td>
                                    <td className="actions-cell">
                                        <button
                                            className="edit-btn"
                                            onClick={() => navigate(`/announcements/edit/${announcement.id}`)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="delete-btn"
                                            onClick={() => handleDelete(announcement.id, announcement.title)}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <style>{`
                .announcements-container {
                    width: 100%;
                    padding: 2rem;
                    box-sizing: border-box;
                }

                .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 2rem;
                    flex-wrap: wrap;
                    gap: 1rem;
                }

                .header h2 {
                    font-size: 2rem;
                    color: var(--text-primary, #1f2937);
                    font-weight: 700;
                    margin: 0;
                }

                .new-announcement-btn {
                    padding: 0.75rem 1.5rem;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 1rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .new-announcement-btn:hover {
                    background: #2563eb;
                }

                .filters-section {
                    display: flex;
                    flex-wrap: nowrap;
                    align-items: flex-end;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                    padding: 1.5rem;
                    background: var(--bg-card, #ffffff);
                    border-radius: 8px;
                    box-shadow: 0 2px 4px var(--shadow, rgba(0, 0, 0, 0.1));
                }

                .filter-group {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    flex-shrink: 0;
                }

                .filter-group:first-child {
                    flex: 1;
                    min-width: 250px;
                }

                .filter-group label {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: var(--text-primary, #374151);
                    white-space: nowrap;
                }

                .search-input {
                    padding: 0.75rem;
                    border: 1px solid var(--border-color, #d1d5db);
                    border-radius: 8px;
                    font-size: 1rem;
                    width: 100%;
                    background: var(--bg-input, #ffffff);
                    color: var(--text-primary, #111827);
                }

                .filter-group select {
                    padding: 0.75rem;
                    border: 1px solid var(--border-color, #d1d5db);
                    border-radius: 8px;
                    font-size: 1rem;
                    background: var(--bg-input, #ffffff);
                    cursor: pointer;
                    min-width: 120px;
                    color: var(--text-primary, #111827);
                }

                .announcements-count {
                    margin-bottom: 1rem;
                    color: var(--text-muted, #6b7280);
                    font-size: 0.9rem;
                }

                .announcements-table-container {
                    background: var(--bg-card, #ffffff);
                    border-radius: 8px;
                    box-shadow: 0 2px 4px var(--shadow, rgba(0, 0, 0, 0.1));
                    overflow-x: auto;
                }

                .announcements-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .announcements-table thead {
                    background: var(--bg-tertiary, #f9fafb);
                }

                .announcements-table th {
                    padding: 1rem;
                    text-align: left;
                    font-weight: 600;
                    color: var(--text-primary, #374151);
                    border-bottom: 2px solid var(--border-color, #e5e7eb);
                }

                .announcements-table td {
                    padding: 1rem;
                    border-bottom: 1px solid var(--border-color, #e5e7eb);
                    vertical-align: top;
                    color: var(--text-primary, #111827);
                }

                .announcements-table tbody tr:hover {
                    background: var(--bg-hover, #f9fafb);
                }

                .title-cell {
                    max-width: 300px;
                }

                .title-cell strong {
                    display: block;
                    margin-bottom: 0.5rem;
                    color: var(--text-primary, #1f2937);
                }

                .description-preview {
                    font-size: 0.875rem;
                    color: var(--text-muted, #6b7280);
                    line-height: 1.5;
                }

                .audience-badge,
                .priority-badge,
                .status-badge {
                    display: inline-block;
                    padding: 0.4rem 0.8rem;
                    border-radius: 20px;
                    font-size: 0.875rem;
                    font-weight: 500;
                }

                .audience-badge.audience-user {
                    background: #dbeafe;
                    color: #1e40af;
                }

                .audience-badge.audience-partner {
                    background: #fce7f3;
                    color: #9f1239;
                }

                .audience-badge.audience-both {
                    background: #fef3c7;
                    color: #92400e;
                }

                .priority-badge.priority-high {
                    background: #fee2e2;
                    color: #991b1b;
                }

                .priority-badge.priority-medium {
                    background: #fef3c7;
                    color: #92400e;
                }

                .priority-badge.priority-low {
                    background: #d1fae5;
                    color: #065f46;
                }

                .status-badge.active {
                    background: #d1fae5;
                    color: #065f46;
                }

                .status-badge.inactive {
                    background: #fee2e2;
                    color: #991b1b;
                }

                .status-toggle {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    cursor: pointer;
                }

                .status-toggle input[type="checkbox"] {
                    cursor: pointer;
                }

                .actions-cell {
                    display: flex;
                    gap: 0.5rem;
                }

                .edit-btn,
                .delete-btn {
                    padding: 0.5rem 1rem;
                    border: none;
                    border-radius: 6px;
                    font-size: 0.875rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .edit-btn {
                    background: #3b82f6;
                    color: white;
                }

                .edit-btn:hover {
                    background: #2563eb;
                }

                .delete-btn {
                    background: #dc2626;
                    color: white;
                }

                .delete-btn:hover {
                    background: #b91c1c;
                }

                .loading-container,
                .error-container,
                .empty-container {
                    text-align: center;
                    padding: 3rem;
                    background: var(--bg-card, #ffffff);
                    border-radius: 8px;
                    box-shadow: 0 2px 4px var(--shadow, rgba(0, 0, 0, 0.1));
                    color: var(--text-muted, #6b7280);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                .loading-container .loader {
                  width: 48px;
                  height: 48px;
                  border-radius: 50%;
                  display: inline-block;
                  position: relative;
                  border: 2px solid #FF3D00;
                  box-sizing: border-box;
                  animation: rotation 1s linear infinite;
                }
                .loading-container .loader::after {
                  content: '';  
                  box-sizing: border-box;
                  position: absolute;
                  left: 4px;
                  top: 4px;
                  border: 2px solid #000;
                  width: 12px;
                  height: 12px;
                  border-radius: 50%;
                }
                [data-theme="dark"] .loading-container .loader::after {
                  border: 2px solid #FFF;
                }
                @keyframes rotation {
                  0% {
                    transform: rotate(0deg);
                  }
                  100% {
                    transform: rotate(360deg);
                  }
                }

                .error-container {
                    color: #dc2626;
                }

                .error-note {
                    margin-top: 1rem;
                    font-size: 0.9rem;
                    color: #6b7280;
                    font-style: italic;
                }

                @media (max-width: 1200px) {
                    .filters-section {
                        flex-wrap: wrap;
                    }
                }

                @media (max-width: 768px) {
                    .announcements-container {
                        padding: 1rem;
                    }

                    .filters-section {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .filter-group {
                        width: 100%;
                    }

                    .filter-group:first-child {
                        min-width: unset;
                    }

                    .search-input {
                        width: 100%;
                    }

                    .announcements-table {
                        font-size: 0.875rem;
                    }

                    .announcements-table th,
                    .announcements-table td {
                        padding: 0.75rem 0.5rem;
                    }

                    .actions-cell {
                        flex-direction: column;
                    }
                }
            `}</style>
        </div>
    );
};

export default Announcements;

